import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Table, Input, Space, Typography, Card, Statistic, Row, Col, Tag, Empty, Spin, Button } from 'antd';
import { SearchOutlined, MinusOutlined, FilterFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Actions
import { get_self_discards, get_self_discards_summary } from '../../../redux/actions/selfActions';
import { get_all_self_rules } from '../../../redux/actions/selfRulesActions';

// Utils
import { get_discard_type_desc } from '../../../utils/get_status_desc';
import { get_discard_type_color } from '../../../utils/get_color_status';

const { Title, Text } = Typography;

const LoadFileDiscards = ({ historyId }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const searchInput = useRef(null);

	// Redux state
	const { file_info, self_discards_loading, self_discards, self_discards_summary_loading, self_discards_summary } = useSelector(state => state.self);
	const { all_self_rules } = useSelector(state => state.self_rules);

	// Local state for pagination and search
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [locationFilter, setLocationFilter] = useState('');
	const [discardTypeFilter, setDiscardTypeFilter] = useState('');

	// Fetch rules when file_info is available
	useEffect(() => {
		if (file_info && file_info.organization && file_info.workspace && file_info.module) {
			const rulesFilters = {
				organization: file_info.organization._id.$oid,
				workspace: file_info.workspace._id.$oid,
				module: file_info.module._id.$oid,
				skip: 0,
				limit: 0
			};
			dispatch(get_all_self_rules(rulesFilters));
		}
	}, [file_info]);

	// Fetch discards on component mount and when filters change
	useEffect(() => {
		if (historyId) {
			fetchDiscards();
		}
	}, [historyId, currentPage, pageSize, locationFilter, discardTypeFilter]);

	const fetchDiscards = () => {
		const filters = {
			load_history: historyId,
			skip: (currentPage - 1) * pageSize,
			limit: pageSize
		};

		// Add server-side filters
		if (locationFilter) {
			filters.location_code = locationFilter;
		}

		if (discardTypeFilter) {
			filters.discard_reason = discardTypeFilter;
		}

		dispatch(get_self_discards(filters));
		dispatch(get_self_discards_summary(historyId));
	};

	// Handle pagination and filter changes
	const handleTableChange = (pagination, filters, sorter) => {
		// Update pagination state
		if (pagination.current !== currentPage) {
			setCurrentPage(pagination.current);
		}
		if (pagination.pageSize !== pageSize) {
			setPageSize(pagination.pageSize);
		}

		// Handle discard type filter - this will trigger useEffect to fetch new data
		if (filters.discard_reason) {
			const newFilter = filters.discard_reason.length > 0 ? filters.discard_reason[0] : '';
			if (newFilter !== discardTypeFilter) {
				setDiscardTypeFilter(newFilter);
				setCurrentPage(1); // Reset to first page when filter changes
			}
		} else if (discardTypeFilter !== '') {
			setDiscardTypeFilter('');
			setCurrentPage(1); // Reset to first page when filter is cleared
		}
	};

	// Search functionality (server-side filtering)
	const handleSearch = (selectedKeys, confirm, dataIndex) => {
		confirm();

		// Update the appropriate filter for server request
		if (dataIndex === 'location_code') {
			setLocationFilter(selectedKeys[0]);
		}

		// Reset to first page when applying filters
		setCurrentPage(1);
	};

	const handleReset = (clearFilters, dataIndex) => {
		clearFilters();

		// Clear the appropriate filter
		if (dataIndex === 'location_code') {
			setLocationFilter('');
		}

		// Reset to first page
		setCurrentPage(1);
	};

	// Get failed rules names from rule IDs
	const getFailedRulesNames = (failedRules) => {
		if (!failedRules || failedRules.length === 0) {
			return '-';
		}

		if (!all_self_rules || !all_self_rules.rules) {
			return '-';
		}

		const ruleNames = failedRules.map(rule => {
			const foundRule = all_self_rules.rules.find(r => r._id.$oid === rule.$oid);
			return foundRule ? foundRule.name : rule.$oid;
		});

		return ruleNames.join(', ');
	};

	// Get column search props
	const getColumnSearchProps = (dataIndex) => ({
		filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
			<div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
				<Input
					ref={searchInput}
					placeholder={t('loadStatusDetails.discards.searchByCode')}
					value={selectedKeys[0]}
					onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
					onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
					style={{ marginBottom: 8, display: 'block' }}
				/>
				<Space>
					<Button
						type="primary"
						onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
						icon={<SearchOutlined />}
						size="small"
						style={{ width: 90 }}
					>
						{t('buttons.search')}
					</Button>
					<Button
						onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
						size="small"
						style={{ width: 90 }}
					>
						{t('buttons.reset')}
					</Button>
					<Button
						type="link"
						size="small"
						onClick={() => {
							close();
						}}
					>
						{t('buttons.close')}
					</Button>
				</Space>
			</div>
		),
		filterIcon: (filtered) => (
			<SearchOutlined style={{ color: filtered ? 'white' : '#818D9C' }} />
		),
		filteredValue: (dataIndex === 'location_code' && locationFilter) ? [locationFilter] : null,
		onFilterDropdownOpenChange: (visible) => {
			if (visible) {
				setTimeout(() => searchInput.current?.select(), 100);
			}
		}
	});

	// Table columns
	const columns = [
		{
			title: t('routesTable.headers.code'),
			dataIndex: 'location_code',
			key: 'location_code',
			width: '20%',
			...getColumnSearchProps('location_code')
		},
		{
			title: t('filters.route'),
			dataIndex: 'route_code',
			key: 'route_code',
			width: '20%'
		},
		{
			title: t('loadStatusDetails.discards.reason'),
			dataIndex: 'discard_reason',
			key: 'discard_reason',
			width: '20%',
			filters:Array.from({ length: 4 }, (_, i) => i + 1).map(type => ({
				text: get_discard_type_desc(type),
				value: type
			})),
			filterSearch: true,
			filterMultiple: false, // Only allow single selection
			filteredValue: discardTypeFilter ? [discardTypeFilter] : null,
			filterIcon: (filtered) => (
				<FilterFilled style={{ color: filtered ? 'white' : '#818D9C' }} />
			),
			onFilter: () => true, // Always return true since we're doing server-side filtering
			render: (type) => (
				<Tag color={get_discard_type_color(type)}>
					{get_discard_type_desc(type)}
				</Tag>
			)
		},
		{
			title: t('loadStatusDetails.discards.details'),
			dataIndex: 'failed_rules',
			key: 'failed_rules',
			// width: '60%',
			render: (failedRules) => (
				<Text>
					{getFailedRulesNames(failedRules)}
				</Text>
			)
		}
	];

	if (!historyId) {
		return (
			<Empty
				description={t('discards.noFileSelected') || 'No file selected'}
			/>
		);
	}

	return (
		<div style={{ paddingTop: '3rem' }}>
			<Title level={4} style={{ textAlign: 'center', marginBottom: '1rem' }}>
				{t('loadStatusDetails.discards.title')}
			</Title>

			{/* Statistics Cards */}
			<Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
				<Col xs={24} sm={12} md={8} lg={4}>
					<Card size="small">
						<Statistic
							title={t('loadStatusDetails.discards.totalDiscards')}
							loading={self_discards_summary_loading}
							value={self_discards_summary?.totals?.total_discarded || 0}
							valueStyle={{ color: '#415577' }}
						/>
					</Card>
				</Col>
				<Col xs={24} sm={12} md={8} lg={5}>
					<Card size="small">
						<Statistic
							title={t('statusDesc.discardType.rules')}
							loading={self_discards_summary_loading}
							value={self_discards_summary?.by_reason?.rules_validation || 0}
							valueStyle={{ color: '#f5222d' }}
						/>
					</Card>
				</Col>
				<Col xs={24} sm={12} md={8} lg={5}>
					<Card size="small">
						<Statistic
							title={t('statusDesc.discardType.inactive')}
							loading={self_discards_summary_loading}
							value={self_discards_summary?.by_reason?.inactive_routes || 0}
							valueStyle={{ color: '#fa541c' }}
						/>
					</Card>
				</Col>
				<Col xs={24} sm={12} md={8} lg={5}>
					<Card size="small">
						<Statistic
							title={t('statusDesc.discardType.invalid')}
							loading={self_discards_summary_loading}
							value={self_discards_summary?.by_reason?.invalid_route_code || 0}
							valueStyle={{ color: '#fa8c16' }}
						/>
					</Card>
				</Col>
				<Col xs={24} sm={12} md={8} lg={5}>
					<Card size="small">
						<Statistic
							title={t('statusDesc.discardType.vectorized')}
							loading={self_discards_summary_loading}
							value={self_discards_summary?.by_reason?.vectorized_prefilter || 0}
							valueStyle={{ color: '#faad14' }}
						/>
					</Card>
				</Col>
			</Row>

			{/* Discards Table */}
			<Spin spinning={self_discards_loading} tip={t('loadStatus.statusTable.loading')} size='large'>
				<Table
					columns={columns}
					dataSource={self_discards?.data || []}
					rowKey={(record) => `${record.location_code}-${record.discard_type}-${record._id?.$oid || Math.random()}`}
					pagination={{
						current: currentPage,
						pageSize: pageSize,
						total: self_discards?.count || 0,
						showSizeChanger: true,
						pageSizeOptions: ['10', '25', '50', '100'],
						showTotal: (total, range) =>
							`${range[0]}-${range[1]} ${t('pagination.of')} ${total}`,
						showQuickJumper: self_discards?.count > 100,
						locale: {
							items_per_page: t('pagination.rows')
						}
					}}
					onChange={handleTableChange}
					scroll={{ x: 800, y: 600 }}
					size="middle"
					locale={{
						emptyText: !self_discards_loading && (
							<Empty
								description={
									<Text type='secondary'>{t('loadStatusDetails.discards.noDiscards') || 'No discards found'}</Text>
								}
							/>
						),
						filterReset: t('buttons.reset'),
						filterSearchPlaceholder: t('buttons.search')
					}}
				/>
			</Spin>
		</div>
	);
};

LoadFileDiscards.propTypes = {
	historyId: PropTypes.string.isRequired
};

export default LoadFileDiscards;
